import { SessionData, DayStats } from '../hooks/useStatistics';
import { HybridStorageService } from './hybridStorageService';
import { User } from './storageTypes';

export interface StatisticsBackupData {
  sessions: SessionData[];
  generatedAt: string;
  version: string;
}

export class EnhancedStatisticsService {
  private readonly SESSIONS_KEY = 'deletoSessions';
  private readonly BACKUP_KEY = 'deletoStatsBackup';
  private readonly VERSION = '1.0.0';
  private storage: HybridStorageService;

  constructor(user: User | null = null) {
    this.storage = new HybridStorageService(user, {
      enableLocalBackup: true,
      syncOnConnect: true
    });
  }

  // Update user context when auth state changes
  setUser(user: User | null): void {
    this.storage.setUser(user);
  }

  /**
   * Get all saved sessions
   */
  async getSessions(): Promise<SessionData[]> {
    try {
      const sessions = await this.storage.getItem<SessionData[]>(this.SESSIONS_KEY);
      return sessions || [];
    } catch (error) {
      console.error('Error loading sessions:', error);
      return [];
    }
  }

  /**
   * Save sessions
   */
  async saveSessions(sessions: SessionData[]): Promise<boolean> {
    try {
      return await this.storage.setItem(this.SESSIONS_KEY, sessions);
    } catch (error) {
      console.error('Error saving sessions:', error);
      return false;
    }
  }

  /**
   * Add a new session with additional statistics data
   */
  async addSession(session: SessionData): Promise<boolean> {
    try {
      const sessions = await this.getSessions();
      
      // Add additional metadata for statistics
      const enhancedSession: SessionData = {
        ...session,
        sessionDuration: session.sessionDuration || 0,
        deletedChars: session.deletedChars || 0,
        deletedWords: session.deletedWords || 0,
      };

      sessions.unshift(enhancedSession);
      return await this.saveSessions(sessions);
    } catch (error) {
      console.error('Error adding session:', error);
      return false;
    }
  }

  /**
   * Delete a session by ID
   */
  async deleteSession(sessionId: number): Promise<boolean> {
    try {
      const sessions = await this.getSessions();
      const filteredSessions = sessions.filter(session => session.id !== sessionId);
      return await this.saveSessions(filteredSessions);
    } catch (error) {
      console.error('Error deleting session:', error);
      return false;
    }
  }

  /**
   * Get a session by ID
   */
  async getSessionById(sessionId: number): Promise<SessionData | null> {
    const sessions = await this.getSessions();
    return sessions.find(session => session.id === sessionId) || null;
  }

  /**
   * Calculate daily statistics from sessions
   */
  async getDailyStats(): Promise<DayStats[]> {
    const sessions = await this.getSessions();
    const dailyStatsMap = new Map<string, DayStats>();

    sessions.forEach(session => {
      const date = new Date(session.date).toISOString().split('T')[0];
      const existing = dailyStatsMap.get(date);

      if (existing) {
        existing.sessions += 1;
        existing.totalWords += session.wordCount || 0;
        existing.totalChars += session.charCount || 0;
        existing.totalTime += session.sessionDuration || 0;
        existing.deletedChars += session.deletedChars || 0;
        existing.deletedWords += session.deletedWords || 0;
      } else {
        dailyStatsMap.set(date, {
          date,
          sessions: 1,
          totalWords: session.wordCount || 0,
          totalChars: session.charCount || 0,
          totalTime: session.sessionDuration || 0,
          deletedChars: session.deletedChars || 0,
          deletedWords: session.deletedWords || 0,
          numTimesDeleto: session.numTimesDeleto || 0,
        });
      }
    });

    return Array.from(dailyStatsMap.values()).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  /**
   * Create a backup of all statistics data
   */
  async createBackup(): Promise<StatisticsBackupData> {
    const sessions = await this.getSessions();
    return {
      sessions,
      generatedAt: new Date().toISOString(),
      version: this.VERSION
    };
  }

  /**
   * Export statistics as downloadable JSON file
   */
  async exportStatistics(): Promise<void> {
    try {
      const backup = await this.createBackup();
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `deleto-statistics-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting statistics:', error);
      throw new Error('Failed to export statistics');
    }
  }

  /**
   * Import statistics from backup file
   */
  async importStatistics(file: File, mergeMode: 'replace' | 'merge' = 'merge'): Promise<boolean> {
    try {
      const text = await file.text();
      const backup: StatisticsBackupData = JSON.parse(text);
      
      // Validate backup format
      if (!backup.sessions || !Array.isArray(backup.sessions)) {
        throw new Error('Invalid backup format');
      }

      if (mergeMode === 'replace') {
        return await this.saveSessions(backup.sessions);
      } else {
        // Merge mode: combine with existing sessions
        const existingSessions = await this.getSessions();
        const existingIds = new Set(existingSessions.map(s => s.id));
        const newSessions = backup.sessions.filter(s => !existingIds.has(s.id));
        const mergedSessions = [...existingSessions, ...newSessions];
        
        // Sort by date (newest first)
        mergedSessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        return await this.saveSessions(mergedSessions);
      }
    } catch (error) {
      console.error('Error importing statistics:', error);
      return false;
    }
  }

  /**
   * Clear all statistics data
   */
  async clearAllData(): Promise<boolean> {
    try {
      await this.storage.removeItem(this.SESSIONS_KEY);
      await this.storage.removeItem(this.BACKUP_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  }

  /**
   * Get storage usage information
   */
  getStorageInfo(): { used: string; available: string; percentage: number } {
    return this.storage.getStorageInfo();
  }

  /**
   * Get sync status for UI display
   */
  getSyncStatus() {
    return this.storage.getSyncStatus();
  }

  /**
   * Check if user is signed in
   */
  isUserSignedIn(): boolean {
    return this.storage.isUserSignedIn();
  }

  /**
   * Manually trigger sync (for UI button)
   */
  async syncToCloud(): Promise<boolean> {
    return await this.storage.syncLocalToRemote();
  }

  // Migration helper for when user signs up
  async migrateExistingData(): Promise<boolean> {
    return await this.storage.syncLocalToRemote();
  }
}

// For now, create a singleton that works without authentication
export const enhancedStatisticsService = new EnhancedStatisticsService();
export default enhancedStatisticsService; 